<template>
	<div class="modal">
		<div class="modal-content">
			<!-- モーダルを閉じるボタン -->
			<span class="close-button" @click="closeModal">&times;</span>

			<!-- モーダルのタイトル -->
			<div class="modal-title">
				<h2>{{ modalTitle }}</h2>
				<!-- 指導記録のモーダルのみ要約ボタンを表示 -->
				<button v-if="title === '指導記録'" class="summary-button" @click="openSumaryOutput">要約出力</button>
			</div>

			<!-- 要約出力画面の表示 -->
			<SummarOutput :visible="isSummaryBar" ref="summaryOutput" :userId="userId" :houseId="houseId" @close="closeSummaryOutput" />

			<!-- ファイル選択セクション -->
			<div class="file-select">
				<input type="file" @change="handleFileSelect" accept=".xlsx" />
			</div>

			<!-- ボタングループ -->
			<div class="button-group">
				<button class="modal-button" @click="showConfirmationModal">OK</button>
				<button class="modal-button" @click="closeModal">キャンセル</button>
			</div>

			<!-- アップロード履歴セクション -->
			<div class="upload-history">
				<h3>アップロード履歴</h3>
				<table>
					<thead>
						<tr>
							<th>日時</th>
							<th>ファイル名</th>
							<th>更新者</th>
						</tr>
					</thead>
					<tbody>
						<!-- 各アップロード履歴アイテムを最大10個表示 -->
						<tr v-for="(item, index) in items.slice(0, 10)" :key="index">
							<td>{{ item.upload_date }}</td>
							<td>
								<span @click="handleFileDownload(item.link)" style="cursor: pointer; color: blue; text-decoration: underline">
									{{ item.file_name }}
								</span>
							</td>
							<td>{{ item.user_name }}</td>
						</tr>
					</tbody>
				</table>
			</div>

			<!-- ローディングオーバーレイの表示 -->
			<div v-if="isLoading" class="loading-overlay">
				<!-- スピナー -->
				<div class="loading-spinner"></div>
			</div>
		</div>

		<!-- 確認モーダル -->
		<ConfirmUpload
			v-if="confirmationModalVisible"
			:fileName="selectedFileName"
			:fileExists="fileExists"
			@close="closeConfirmationModal"
			@confirm="uploadFile"
		/>
	</div>
</template>

<script>
import ConfirmUpload from "./ConfirmUpload.vue";
import SummarOutput from "./SummaryOutput.vue";
import { useBlobStore } from "@/stores/blobStore";
import { downloadFile } from "@/utils/fileDownload"; // ダウンロード処理をインポート
import api from "@/utils/api";

export default {
	name: "UploadModal",
	components: {
		ConfirmUpload,
		SummarOutput,
	},
	props: {
		title: String, // モーダルタイトル
		userId: String,
		houseId: String,
		userName: String,
	},
	data() {
		return {
			selectedFile: null, // 選択されたファイル
			confirmationModalVisible: false, // 確認モーダルの表示状態
			isLoading: false, // ローディング状態を管理
			fileExists: false, // ファイルの存在チェックフラグ
			directory: null, // アップロード先のディレクトリ
			// アップロード履歴の初期データ
			items: [], // items 配列を初期化
			// ディレクトリ名をマッピングするオブジェクト
			directoryMap: {
				指導記録: "guidance_record",
				養液調査: "nutrient_solution",
				糖度: "brix_state",
				給液管理: "liquid_supply_management",
				"果実・収量サイズ": "gross_harvest",
				生育調査: "growth_data",
			},
			isSummaryBar: false,
		};
	},
	mounted() {
		this.getUploadHistory();
	},
	computed: {
		// モーダルタイトル
		modalTitle() {
			return `${this.title} データ更新`;
		},
		// 選択されたファイル名
		selectedFileName() {
			return this.selectedFile ? this.selectedFile.name : "";
		},
	},
	methods: {
		// ファイルをダウンロードするメソッド
		async handleFileDownload(fileLink) {
			this.isLoading = true; // ローディング開始

			try {
				await downloadFile(fileLink, this.accessToken); // 共通メソッドを呼び出す
			} catch (error) {
				console.error(error.message); // エラーメッセージを設定
			} finally {
				this.isLoading = false; // ローディング終了
			}
		},
		// アップロード履歴を取得するメソッド
		getUploadHistory() {
			const blobStore = useBlobStore(); // ストアのインスタンスを取得
			const history = blobStore.uploadHistory;
			this.directory = this.directoryMap[this.title] || ""; // タイトルに応じたディレクトリ名を取得

			// ディレクトリ名に一致する履歴オブジェクトを items に格納
			if (this.directory && history[this.directory]) {
				this.items = history[this.directory].map((item) => ({
					file_name: item.file_name.split("/").pop(), // スラッシュで分割してファイル名のみを取得
					upload_date: item.upload_date,
					user_name: item.user_name,
					link: `${process.env.VUE_APP_BLOB_BASE_URL}${this.houseId}/${this.directory}/${item.file_name}`,
				}));
			} else {
				this.items = []; // 一致するディレクトリが無ければ空にする
			}
		},

		// モーダルを閉じるメソッド
		closeModal() {
			this.directory = null;
			this.$emit("close");
		},

		// ファイル選択時の処理
		handleFileSelect(event) {
			const files = event.target.files;
			if (files.length > 0) {
				this.selectedFile = files[0]; // 選択されたファイルをセット
			} else {
				console.error("No file selected."); // ファイル未選択のエラーメッセージ
				this.selectedFile = null;
			}
		},

		// アップロード確認モーダルを表示するメソッド
		async showConfirmationModal() {
			if (this.selectedFile) {
				const blobStore = useBlobStore(); // ストアのインスタンスを取得

				// 選択したファイル名がアップロード可能なファイルかを確認
				const regex = new RegExp(`^${this.title}\\(\\d+\\)\\.xlsx$`);
				if (!regex.test(this.selectedFile.name)) {
					if (!this.selectedFile.name.startsWith(this.title)) {
						alert(`このファイル名ではアップロードできません。`);
					} else {
						alert(`ファイル名は「${this.title}(数字).xlsx」の形式である必要があります。`);
					}
					return; // 条件に合わない場合は処理を中止
				}

				// ストアのitemsからディレクトリのアイテムを取得
				const itemsInDirectory = blobStore.blob_files[this.directory];

				// selectedFile.nameが存在するか確認
				this.fileExists = false; // 初期値を false に設定
				// selectedFile.nameが存在するか確認
				if (itemsInDirectory) {
					// 選択したファイル名がディレクトリ内に存在するか確認
					for (const filePath of itemsInDirectory) {
						const fileName = filePath.split("/").pop(); // ファイル名を抽出
						// console.log(this.selectedFile.name);
						if (fileName === this.selectedFile.name) {
							this.fileExists = true; // 一致するファイル名があればfileExistsをtrueに設定
							break; // 一致が見つかったらループを抜ける
						}
					}
				} else {
					console.log(`Directory ${this.directory} does not exist in items.`);
				}

				this.confirmationModalVisible = true; // 確認モーダルを表示
			} else {
				alert("ファイルを選択してください。"); // ファイル未選択の場合のアラート
			}
		},

		// アップロード確認モーダルを閉じるメソッド
		closeConfirmationModal() {
			this.confirmationModalVisible = false; // 確認モーダルを非表示
		},

		// ファイルのアップロード処理
		async uploadFile() {
			if (!this.selectedFile) {
				console.error("File is undefined"); // 未選択ファイルのエラーメッセージ表示
				return;
			}

			// アップロード開始前にローディング開始
			this.isLoading = true;

			// 現在の日本時間を取得する関数
			const getCurrentDateTimeJST = () => {
				const utcDate = new Date(); // 現在のUTC日時を取得
				const jstOffset = 9 * 60; // 日本時間はUTC+9時間
				const jstDate = new Date(utcDate.getTime() + jstOffset * 60 * 1000); // JSTに変換
				return jstDate.toISOString(); // ISO形式の日時を取得
			};
			const uploadDateTime = getCurrentDateTimeJST(); // アップロード時の日時を取得

			try {
				// アップロード用のURLを取得
				const response = await api.get("", {
					params: {
						action: "GetUploadBlobURL",
						blob_name: this.selectedFile.name,
						directory: this.directory, // 指定したディレクトリを追加
						house_id: this.houseId, // 圃場IDを追加
						date_time: uploadDateTime,
					},
				});

				const uploadUrl = response.data.upload_url; // アップロードURLを取得
				// console.log("Upload URL:", uploadUrl);

				// SAS URLを使ってファイルをアップロード
				const uploadResponse = await fetch(uploadUrl, {
					method: "PUT",
					headers: {
						"x-ms-blob-type": "BlockBlob", // Blobのタイプを指定
					},
					body: this.selectedFile, // アップロードするファイル
				});

				if (uploadResponse.ok) {
					console.log("Upload successful!"); // アップロード成功メッセージ
					this.$emit("upload-success", this.selectedFile.name); // アップロード成功イベントを親に通知

					// CosmosDBにファイル情報を保存
					const dbSuccess = await this.logUploadInfo(uploadDateTime); // アップロード日時を渡す
					if (dbSuccess) {
						this.$emit("db-upload-success", this.selectedFile.name); // DB登録成功イベントを親に通知
					} else {
						this.$emit("db-upload-fail", this.selectedFile.name); // DB登録失敗イベントを親に通知
					}
				} else {
					console.error("Upload failed!"); // アップロード失敗メッセージ
					const errorMessage = await uploadResponse.text(); // エラーメッセージを取得
					console.error("Error details:", errorMessage);
					this.$emit("upload-fail", this.selectedFile.name); // アップロード失敗イベントを親コンポーネントに通知
				}
			} catch (error) {
				console.error("Upload error:", error); // アップロード中のエラーメッセージ
				this.$emit("upload-fail", this.selectedFile.name); // エラー発生時も失敗イベントを通知
			} finally {
				this.closeModal(); // アップロード完了後にモーダルを閉じる
				this.isLoading = false; // アップロード処理が終了したらローディングをオフに
			}
		},

		// CosmosDBにアップロード情報を記録するメソッド
		async logUploadInfo(uploadDateTime) {
			// ユーザー名をスラッシュで分割し、最初の部分を取得
			// const userName = this.userName.split("/")[0].trim();

			// パースしたuploadDateTimeから年月日時間を取得
			const uploadDate = new Date(uploadDateTime);
			const year = uploadDate.getUTCFullYear();
			const month = String(uploadDate.getUTCMonth() + 1).padStart(2, "0"); // 月は0から始まるので+1
			const day = String(uploadDate.getUTCDate()).padStart(2, "0");
			const hour = String(uploadDate.getUTCHours()).padStart(2, "0");
			const minute = String(uploadDate.getUTCMinutes()).padStart(2, "0");

			// 最終的なuploadDateを作成
			const formattedUploadDate = `${year}/${month}/${day} ${hour}:${minute}`; // "YYYY/MM/DD HH:mm" 形式に整形

			const uploadData = {
				house_id: this.houseId,
				file_name: uploadDateTime + "/" + this.selectedFile.name, // アップロードするファイル名
				directory: this.directory,
				upload_date: formattedUploadDate, // アップロードの日付（ISOフォーマット）
			};
			// console.log("DB登録情報：", uploadData);
			try {
				const response = await api.post("", uploadData, {
					params: {
						action: "PostUploadHistory", // actionをparamsに追加
					},
				});
				console.log("Upload info logged to CosmosDB:", response.data); // ログの成功メッセージ
				return true; // DB登録が成功したら true を返す
			} catch (error) {
				console.error("Error logging upload info to CosmosDB:", error); // CosmosDBへのログ記録中のエラーメッセージ
				return false; // DB登録が失敗したら false を返す
			}
		},

		openSumaryOutput() {
			this.isSummaryBar = true;
			this.$refs.summaryOutput.checkExpiry(); // refを使って子コンポーネントにアクセス
		},
		closeSummaryOutput() {
			this.isSummaryBar = false;
		},
	},
};
</script>

<style scoped>
/* 画面全体 */
body {
	padding: 0; /* ページ全体のパディングをリセット */
	margin: 0; /* ページ全体のマージンをリセット */
	background-color: red; /* 背景色を赤に設定（必要に応じて変更） */
}

/* モーダル全体のスタイル */
.modal {
	display: flex; /* フレックスボックスで配置 */
	justify-content: center; /* コンテンツを中央に配置 */
	align-items: center; /* コンテンツを垂直方向に中央に配置 */
	position: fixed; /* 固定位置で表示 */
	top: 0; /* 上端を画面の0に設定 */
	left: 0; /* 左端を画面の0に設定 */
	width: 100%; /* 幅を100%に設定 */
	height: 100%; /* 高さを100%に設定 */
	background: rgba(0, 0, 0, 0.5); /* 半透明の背景 */
}

/* モーダルコンテンツのスタイル */
.modal-content {
	position: relative;
	background: white; /* モーダルの背景色を白に設定 */
	padding: 0px; /* 内部パディングをリセット */
	margin: 0px; /* 内部マージンをリセット */
	border-radius: 5px; /* 角を丸くする */
	box-shadow: 0 0 10px rgba(0, 0, 0, 0.3); /* モーダルに影を付けて浮き上がらせる */
	width: 80%; /* 幅を80%に設定 */
	max-width: 800px; /* 最大幅を800pxに設定 */
	max-height: 80%; /* 高さをautoに設定 */
	min-height: 400px; /* 要約サイドバーの高さに合わせて設定 */
	overflow-y: auto; /* 内容がオーバーフローした時にスクロール可能にする */
}

/* モーダルタイトルのスタイル */
.modal-title {
	background-color: rgb(136, 174, 82); /* タイトルの背景色 */
	padding: 15px; /* 上下左右にパディングを設定 */
	display: flex;
	justify-content: space-between;
	width: 100%;
	box-sizing: border-box;
}

/* クローズボタンのスタイル */
.close-button {
	position: absolute;
	top: 10px;
	right: 10px;
	cursor: pointer; /* マウスカーソルをポインターに設定 */
	font-weight: bold; /* 太字 */
	z-index: 100;
}

/* 要約出力ボタンのスタイル */
.summary-button {
	background-color: rgb(136, 174, 82); /* ボタンの背景色 */
	border: outset 2px black; /* ボタンにボーダーを付けない */
	border-radius: 5px; /* ボタンの角を丸くする */
	cursor: pointer; /* マウスカーソルをポインターに設定 */
	padding: 5px 15px;
	margin-right: 30px;
}

/* ファイル選択のスタイル */
.file-select {
	margin: 30px; /* セクション全体にマージンを設定 */
}

/* ボタングループのスタイル */
.button-group {
	display: flex; /* フレックスボックスでボタンを配置 */
	justify-content: flex-end; /* ボタンを右寄せ */
	margin-top: 20px; /* ボタンの上にスペースを追加 */
}

/* モーダルボタンのスタイル */
.modal-button {
	flex: 1; /* ボタンが等しく広がるように設定 */
	max-width: 120px; /* ボタンの最大幅を指定 */
	margin-right: 10px; /* ボタン間にスペースを設定 */
	padding: 10px 0; /* 縦方向のパディングを設定 */
	background-color: rgb(136, 174, 82); /* ボタンの背景色 */
	color: black; /* ボタンの文字色 */
	border: none; /* ボタンにボーダーを付けない */
	border-radius: 5px; /* ボタンの角を丸くする */
	cursor: pointer; /* マウスカーソルをポインターに設定 */
}

/* アップロード履歴のスタイル */
.upload-history {
	margin: 30px; /* セクション全体にマージンを設定 */
}

/* テーブルスタイル */
table {
	width: 100%; /* テーブルが親要素の幅いっぱいに設定 */
	border-collapse: collapse; /* テーブルのボーダーを重ねる */
	overflow-y: auto; /* 縦方向のオーバーフローをスクロール可能にする */
}

th,
td {
	border: 1px solid #dddddd; /* セルのボーダーを設定 */
	text-align: left; /* テキストを左揃え */
	padding: 8px; /* セル内のパディングを設定 */
	background-color: white; /* セルの背景色を白に設定 */
}

th {
	background-color: rgb(15, 74, 104); /* ヘッダーの背景色 */
	color: white; /* ヘッダーのテキスト色 */
}

/* ローディングオーバーレイのスタイル */
.loading-overlay {
	position: fixed; /* 固定位置で表示 */
	top: 0; /* 上端を画面の0に設定 */
	left: 0; /* 左端を画面の0に設定 */
	right: 0; /* 右端を画面の100%に設定 */
	bottom: 0; /* 下端を画面の0に設定 */
	background: rgba(128, 128, 128, 0.5); /* グレーの背景、透明度50%でオーバーレイ表示 */
	display: flex; /* フレックスボックスで配置 */
	justify-content: center; /* コンテンツを中央に配置 */
	align-items: center; /* コンテンツを垂直方向に中央に配置 */
	z-index: 1000; /* 他の要素より前に表示 */
}

/* ローディングスピナーのスタイル */
.loading-spinner {
	width: 30px; /* スピナーのサイズ（幅） */
	height: 30px; /* スピナーのサイズ（高さ） */
	border: 4px solid rgba(0, 0, 0, 0.1); /* スピナーの外周の色 */
	border-top-color: #000; /* スピナーの回転部分の色 */
	border-radius: 50%; /* スピナーを円形にする */
	animation: spin 1s linear infinite; /* スピナーに回転アニメーションを設定 */
	margin: 10px auto; /* 中央寄せ */
}

@keyframes spin {
	to {
		transform: rotate(360deg); /* 360度回転するアニメーション */
	}
}
</style>
